import { TSnippetStatus } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

type TSnippetsStatusListProps = {
  snippets: TSnippetStatus[];
  className?: string;
};

export function SnippetsStatusList({ snippets, className }: TSnippetsStatusListProps) {
  const { isLabs } = useMeStore();

  if (!snippets || snippets.length === 0) return null;

  // Only show if in labs mode and at least one snippet is not pending
  const hasNonPendingSnippet = snippets.some((snippet) => snippet.status !== "pending");
  if (!isLabs || !hasNonPendingSnippet) return null;

  const getStatusColor = (status: TSnippetStatus["status"]) => {
    switch (status) {
      case "done":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "processing":
        return "bg-blue-500";
      case "error":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusText = (status: TSnippetStatus["status"]) => {
    switch (status) {
      case "done":
        return "Done";
      case "pending":
        return "Pending";
      case "processing":
        return "Processing";
      case "error":
        return "Error";
      default:
        return "Unknown";
    }
  };

  return (
    <div className={cn("mt-4 w-full max-w-md mx-auto", className)}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Snippet Status</h3>
        <div className="space-y-2 max-h-[35vh] overflow-y-auto">
          {snippets.map((snippet) => (
            <div
              key={snippet.slug}
              className="flex items-start gap-2 p-2 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="text-xs font-mono text-gray-700 break-words flex-1 min-w-0">{snippet.slug}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={cn("w-2 h-2 rounded-full mt-1", getStatusColor(snippet.status))} />
                <span className="text-xs text-gray-600 whitespace-nowrap">{getStatusText(snippet.status)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
